import { useEffect, useState } from "react";
import { AlertTriangle, BarChart3, DollarSign, Download, Lock, LogOut, Package, ShieldCheck, ShoppingBag, TrendingUp, Truck, Users } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const adminGet = (path) => api.get(path, { withCredentials: true }).then((r) => r.data);

const Kpi = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-2xl border border-voltaic-line bg-voltaic-surface/60 p-5">
    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">
      <Icon size={13} className="text-voltaic-cyan" /> {label}
    </div>
    <p className="mt-2 font-display text-2xl font-extrabold sm:text-3xl">{value}</p>
    {sub && <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-voltaic-muted">{sub}</p>}
  </div>
);

const STATUS_LABEL = { paid: "Paid", pending_payment: "Pending", shipped: "Shipped" };
const STATUS_CLS = {
  paid: "text-voltaic-green",
  pending_payment: "text-voltaic-amber",
  shipped: "text-voltaic-cyan",
};

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [shippingId, setShippingId] = useState("");

  useEffect(() => {
    api.get("/auth/me", { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (!user) return;
    Promise.all([adminGet("/admin/stats"), adminGet("/admin/orders"), adminGet("/admin/referrals")])
      .then(([s, o, r]) => {
        setStats(s);
        setOrders(o);
        setReferrals(r);
      })
      .catch(() => toast.error("Could not load dashboard data"));
  }, [user]);

  const login = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password }, { withCredentials: true });
      setUser(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  const logout = async () => {
    await api.post("/auth/logout", {}, { withCredentials: true }).catch(() => {});
    setUser(null);
    setStats(null);
  };

  const markShipped = async (orderId) => {
    setShippingId(orderId);
    try {
      await api.post(`/admin/orders/${orderId}/ship`, {}, { withCredentials: true });
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? { ...o, status: "shipped" } : o)));
      toast.success(`${orderId} marked as shipped`);
    } catch {
      toast.error("Could not update order");
    }
    setShippingId("");
  };

  if (user === undefined) {
    return <main className="flex min-h-screen items-center justify-center"><span className="h-10 w-10 animate-spin rounded-full border-2 border-voltaic-cyan/30 border-t-voltaic-cyan" /></main>;
  }

  if (!user) {
    return (
      <main className="flex min-h-screen items-center justify-center px-5" data-testid="admin-login">
        <Reveal className="w-full max-w-sm">
          <div className="rounded-3xl border border-voltaic-line bg-voltaic-surface/70 p-8">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-voltaic-cyan/10 text-voltaic-cyan">
              <Lock size={20} />
            </span>
            <h1 className="mt-5 font-display text-2xl font-extrabold uppercase">VOLTAIC Ops</h1>
            <p className="mt-1 text-sm text-voltaic-muted">Private admin dashboard. Authorized crew only.</p>
            <div className="mt-6 space-y-3">
              <input data-testid="admin-email-input" type="email" className="field" placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <input data-testid="admin-password-input" type="password" className="field" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} />
              {error && <p data-testid="admin-login-error" className="text-sm text-voltaic-pink">{error}</p>}
              <button data-testid="admin-login-button" onClick={login} disabled={busy} className="w-full rounded-full bg-voltaic-cyan py-3.5 font-display font-bold uppercase tracking-wide text-voltaic-bg transition-shadow hover:glow-cyan disabled:opacity-50">
                {busy ? "Verifying..." : "Enter the vault"}
              </button>
            </div>
          </div>
        </Reveal>
      </main>
    );
  }

  const maxRevenue = Math.max(...(stats?.revenue_by_day || []).map((d) => d.revenue), 1);

  return (
    <main className="mx-auto max-w-7xl px-5 pb-24 pt-32 lg:px-10" data-testid="admin-dashboard">
      <Reveal>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-cyan">
              <ShieldCheck size={14} /> VOLTAIC Ops // {user.email}
            </p>
            <h1 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">Sales dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <a data-testid="export-orders-button" href={`${API}/admin/export/orders`} className="flex items-center gap-2 rounded-full border border-voltaic-line px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-voltaic-muted transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan">
              <Download size={13} /> Orders CSV
            </a>
            <a data-testid="export-referrals-button" href={`${API}/admin/export/referrals`} className="flex items-center gap-2 rounded-full border border-voltaic-line px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-voltaic-muted transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan">
              <Download size={13} /> Referrals CSV
            </a>
            <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 rounded-full border border-voltaic-line px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-voltaic-muted transition-colors hover:border-voltaic-pink hover:text-voltaic-pink">
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>
      </Reveal>

      {stats && (
        <>
          <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-kpis">
            <Kpi icon={DollarSign} label="Revenue" value={money(stats.revenue_total)} sub={`${stats.paid_orders} paid orders`} />
            <Kpi icon={ShoppingBag} label="Orders" value={stats.orders_total} sub={`${stats.pending_orders} awaiting payment`} />
            <Kpi icon={TrendingUp} label="Avg order" value={money(stats.avg_order_value)} sub="Per paid order" />
            <Kpi icon={Users} label="Referrals" value={referrals.reduce((s, r) => s + (r.uses || 0), 0)} sub={`${referrals.length} codes issued`} />
          </div>

          {stats.low_stock?.length > 0 && (
            <Reveal delay={0.03}>
              <div className="mt-6 rounded-3xl border border-voltaic-amber/40 bg-voltaic-amber/5 p-6" data-testid="admin-low-stock">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-voltaic-amber">
                  <AlertTriangle size={14} /> Low stock — restock soon
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {stats.low_stock.map((p) => (
                    <span key={p.id} data-testid={`low-stock-${p.id}`} className="flex items-center gap-2 rounded-full border border-voltaic-amber/40 bg-voltaic-amber/10 px-4 py-2 text-xs">
                      <span className="font-semibold">{p.name}</span>
                      <span className={`font-mono ${p.stock <= 3 ? "text-voltaic-pink" : "text-voltaic-amber"}`}>{p.stock} left</span>
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          <Reveal delay={0.05}>
            <div className="mt-6 rounded-3xl border border-voltaic-line bg-voltaic-surface/60 p-6 sm:p-8" data-testid="admin-revenue-chart">
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-voltaic-cyan">
                <BarChart3 size={14} /> Revenue — last 14 days
              </p>
              <div className="mt-6 flex h-40 items-end gap-1.5">
                {stats.revenue_by_day.map((d) => (
                  <div key={d.date} className="group flex flex-1 flex-col items-center gap-2">
                    <span className="font-mono text-[9px] text-voltaic-muted opacity-0 transition-opacity group-hover:opacity-100">{money(d.revenue)}</span>
                    <div
                      className={`w-full rounded-t-md transition-all ${d.revenue > 0 ? "bg-voltaic-cyan/80 group-hover:bg-voltaic-cyan" : "bg-voltaic-line"}`}
                      style={{ height: `${Math.max(4, (d.revenue / maxRevenue) * 120)}px` }}
                    />
                    <span className="font-mono text-[8px] uppercase text-voltaic-muted">{d.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Reveal delay={0.08}>
              <div className="rounded-3xl border border-voltaic-line bg-voltaic-surface/60 p-6 sm:p-8" data-testid="admin-recent-orders">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-voltaic-cyan">
                  <Package size={14} /> Recent orders
                </p>
                <div className="mt-5 space-y-3">
                  {orders.length === 0 && <p className="text-sm text-voltaic-muted">No orders yet.</p>}
                  {orders.slice(0, 8).map((o) => (
                    <div key={o.order_id} className="flex items-center justify-between gap-3 rounded-xl border border-voltaic-line/60 px-4 py-3" data-testid={`admin-order-${o.order_id}`}>
                      <div className="min-w-0">
                        <p className="truncate font-mono text-xs tracking-wider text-voltaic-cyan">{o.order_id}</p>
                        <p className="mt-0.5 truncate text-xs text-voltaic-muted">{o.customer?.email} · {o.items?.length} item{o.items?.length === 1 ? "" : "s"}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        {o.status === "paid" && (
                          <button
                            data-testid={`ship-order-${o.order_id}`}
                            onClick={() => markShipped(o.order_id)}
                            disabled={shippingId === o.order_id}
                            className="flex items-center gap-1.5 rounded-full border border-voltaic-cyan/50 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-voltaic-cyan transition-colors hover:bg-voltaic-cyan/10 disabled:opacity-50"
                          >
                            <Truck size={11} /> {shippingId === o.order_id ? "..." : "Ship"}
                          </button>
                        )}
                        <div className="text-right">
                          <p className="text-sm font-bold">{money(o.subtotal - (o.discount || 0) + (o.shipping || 0))}</p>
                          <p className={`font-mono text-[9px] uppercase tracking-[0.15em] ${STATUS_CLS[o.status] || "text-voltaic-muted"}`}>{STATUS_LABEL[o.status] || o.status}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="rounded-3xl border border-voltaic-line bg-voltaic-surface/60 p-6 sm:p-8" data-testid="admin-top-referrals">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-voltaic-cyan">
                  <Users size={14} /> Top referral performers
                </p>
                <div className="mt-5 space-y-3">
                  {referrals.length === 0 && <p className="text-sm text-voltaic-muted">No referral codes issued yet.</p>}
                  {referrals.slice(0, 8).map((r, idx) => (
                    <div key={r.code} className="flex items-center justify-between gap-3 rounded-xl border border-voltaic-line/60 px-4 py-3" data-testid={`admin-referral-${r.code}`}>
                      <div className="flex items-center gap-3">
                        <span className="grid h-7 w-7 place-items-center rounded-full border border-voltaic-line font-mono text-[10px] text-voltaic-muted">{idx + 1}</span>
                        <div>
                          <p className="font-mono text-xs font-bold tracking-[0.15em] text-voltaic-cyan">{r.code}</p>
                          <p className="mt-0.5 truncate text-xs text-voltaic-muted">{r.owner_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold">{r.uses} sales</p>
                        <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-voltaic-muted">{r.credits} credits left</p>
                      </div>
                    </div>
                  ))}
                </div>
                {stats.top_products.length > 0 && (
                  <div className="mt-6 border-t border-voltaic-line pt-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-voltaic-muted">Best sellers by revenue</p>
                    <div className="mt-3 space-y-2">
                      {stats.top_products.map((p) => (
                        <div key={p.name} className="flex items-center justify-between text-sm">
                          <span className="truncate">{p.name}</span>
                          <span className="font-mono text-xs text-voltaic-muted">{p.qty} sold · <span className="text-voltaic-cyan">{money(p.revenue)}</span></span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </>
      )}
    </main>
  );
}
